import axios from 'axios';
import { useState, useEffect } from 'react';

// API base configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 10000,
});

// Request interceptor for authentication
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      localStorage.removeItem('userRole');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Generic API request function
export const apiRequest = async (method, endpoint, data = null, config = {}) => {
  try {
    const response = await apiClient.request({
      method,
      url: endpoint,
      data,
      ...config,
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error(`API ${method} ${endpoint} error:`, error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Request failed',
      status: error.response?.status,
    };
  }
};

// Convenience methods
export const apiGet = (endpoint, config = {}) => apiRequest('GET', endpoint, null, config);
export const apiPost = (endpoint, data = null, config = {}) => apiRequest('POST', endpoint, data, config);
export const apiPut = (endpoint, data = null, config = {}) => apiRequest('PUT', endpoint, data, config);
export const apiDelete = (endpoint, config = {}) => apiRequest('DELETE', endpoint, null, config);

// Authentication utilities
export const getAuthToken = () => localStorage.getItem('token');
export const setAuthToken = (token) => localStorage.setItem('token', token);
export const removeAuthToken = () => localStorage.removeItem('token');

export const getUserRole = () => localStorage.getItem('userRole');
export const setUserRole = (role) => localStorage.setItem('userRole', role);
export const removeUserRole = () => localStorage.removeItem('userRole');

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};

// Logout utility
export const logout = () => {
  removeAuthToken();
  removeUserRole();
  window.location.href = '/login';
};

// API endpoints configuration
export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    LOGOUT: '/api/auth/logout',
    VERIFY_OTP: '/api/auth/verify-otp',
    GET_OTP: '/api/auth/get-otp',
    USER: '/api/auth/user',
    FORGOT_PASSWORD: '/api/auth/forgot-password',
    RESET_PASSWORD: '/api/auth/reset-password',
  },
  
  // Results endpoints
  RESULTS: {
    ENHANCED_DASHBOARD: '/api/results/enhanced-dashboard',
    TEST_RESULT: '/api/results/test-result',
    PERFORMANCE_OVERVIEW: '/api/results/performance-overview',
    SUBJECT_DETAIL: '/api/results/subject-detail',
  },
  
  // Tests endpoints
  TESTS: {
    LIST: '/api/tests',
    DETAILS: '/api/tests/details',
    START: '/api/tests/start',
    SUBMIT: '/api/tests/submit',
    ATTEMPTS: '/api/tests/attempts',
  },
  
  // Questions endpoints
  QUESTIONS: {
    LIST: '/api/questions',
    DETAILS: '/api/questions/details',
  },
  
  // User management endpoints
  USERS: {
    LIST: '/api/users',
    CREATE: '/api/users',
    UPDATE: '/api/users',
    DELETE: '/api/users',
    ROLES: '/api/users/roles',
  },
  
  // Features endpoints
  FEATURES: {
    MY_FEATURES: '/api/features/my-features',
  },
  
  // AI endpoints
  AI: {
    CHAT: '/api/ai/chat',
  },
};

// Hook for API calls with loading and error states
export const useApiCall = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const callApi = async (apiFunction, ...args) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiFunction(...args);
      if (!result.success) {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  return { callApi, loading, error, setError };
};

// Custom hook for data fetching
export const useDataFetching = (endpoint, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      const result = await apiGet(endpoint);
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error);
      }
      setLoading(false);
    };

    fetchData();
  }, dependencies);

  return { data, loading, error };
};

// File upload utility
export const uploadFile = async (file, endpoint, onProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await apiClient.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Upload failed',
    };
  }
};

// Download file utility
export const downloadFile = async (endpoint, filename = 'download') => {
  try {
    const response = await apiClient.get(endpoint, {
      responseType: 'blob',
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Download failed',
    };
  }
};

export default apiClient; 